import { useEffect, useState } from "react";
import { Gift, Star, Sparkles, DollarSign, Crown, Zap, Copy, Loader2, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import { api, apiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { toast } from "sonner";

const TIER_STYLE = {
  Bronze: { ring: "#CD7F32", label: "Bronze" },
  Silver: { ring: "#9CA3AF", label: "Silver" },
  Gold: { ring: "#E8956B", label: "Gold" },
};
const REWARD_ICON = { credit: DollarSign, boosts: Zap, vip: Crown };

export default function Rewards({ embedded = false }) {
  const { user, refreshUser } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [data, setData] = useState(null);
  const [redeeming, setRedeeming] = useState("");

  usePageMeta(
    "Rewards — Earn & Redeem Points | Beauty Trade Club",
    "Earn points on every purchase, review and referral. Redeem for store credit, AI photo boosts or free VIP months.",
    "/rewards",
    !embedded
  );

  const load = () => api.get("/loyalty/me").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { if (user) load(); }, [user]);

  const redeem = async (id) => {
    setRedeeming(id);
    try {
      const { data: res } = await api.post("/loyalty/redeem", { reward_id: id });
      toast.success(res.message);
      await load();
      await refreshUser();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRedeeming(""); }
  };

  const copyRef = () => {
    navigator.clipboard.writeText(`${window.location.origin}/?ref=${data.referral_code}`);
    toast.success("Referral link copied 💌");
  };

  if (!user) {
    return (
      <div className={embedded ? "max-w-md mx-auto px-6 py-10 text-center" : "max-w-md mx-auto px-6 py-24 text-center"} data-testid="rewards-page">
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
        <Gift size={40} className="text-[#E8956B] mx-auto mb-4" />
        <h1 className="font-display text-3xl font-black text-neutral-900">Beauty Club Rewards</h1>
        <p className="text-neutral-500 mt-3">Sign in to earn points on every purchase, review &amp; referral — then redeem for store credit, photo boosts or free VIP.</p>
        <button data-testid="rewards-signin-btn" onClick={() => setAuthOpen(true)} className="mt-6 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-8 py-3 transition-all">Sign in to start earning</button>
      </div>
    );
  }

  if (!data) return <div className="py-24 text-center text-neutral-400"><Loader2 className="animate-spin mx-auto" /></div>;

  const tier = data.tier;
  const style = TIER_STYLE[tier.name] || TIER_STYLE.Bronze;
  const progress = tier.next ? Math.min(100, Math.round((data.points_lifetime / tier.next.at) * 100)) : 100;
  const ptVal = data.point_value_usd;

  return (
    <div className={embedded ? "max-w-5xl mx-auto px-0 pt-4" : "max-w-5xl mx-auto px-6 py-12"} data-testid="rewards-page">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 text-[#E8956B] text-xs font-extrabold uppercase tracking-[0.3em] mb-3"><Gift size={14} /> Beauty Club Rewards</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black text-neutral-900">Earn points, get treated 💗</h1>
      </div>

      {/* BALANCE + TIER */}
      <div className="grid md:grid-cols-3 gap-5 mb-10">
        <div data-testid="points-balance-card" className="bg-gradient-to-br from-[#E8956B] to-[#CE744A] text-white rounded-3xl p-6">
          <div className="text-xs uppercase tracking-wider text-white/80 font-bold">Your points</div>
          <div className="text-5xl font-black mt-1" data-testid="points-value">{data.points.toLocaleString()}</div>
          <div className="text-sm text-white/85 mt-1">≈ ${(data.points * ptVal).toFixed(2)} in rewards</div>
        </div>
        <div className="bg-white border border-neutral-200 rounded-3xl p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 font-bold"><Star size={13} style={{ color: style.ring }} fill={style.ring} /> Tier</div>
          <div className="text-3xl font-black text-neutral-900 mt-1" data-testid="tier-name">{style.label}</div>
          {tier.next ? (
            <>
              <div className="mt-3 h-2 bg-neutral-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, background: style.ring }} />
              </div>
              <div className="text-xs text-neutral-500 mt-1">{Math.max(0, tier.next.at - data.points_lifetime)} pts to {tier.next.name}</div>
            </>
          ) : <div className="text-xs text-neutral-500 mt-3">Top tier — you earn +25% points! 🎉</div>}
        </div>
        <div className="bg-white border border-neutral-200 rounded-3xl p-6">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 font-bold"><DollarSign size={13} className="text-green-600" /> Store credit</div>
          <div className="text-3xl font-black text-neutral-900 mt-1" data-testid="store-credit-value">${data.store_credit.toFixed(2)}</div>
          <div className="text-xs text-neutral-500 mt-1">Applied automatically at checkout</div>
        </div>
      </div>

      {/* REWARDS CATALOG */}
      <div data-testid="first-order-booster" className="mb-6 rounded-2xl bg-gradient-to-r from-[#E8956B] to-[#CE744A] text-white px-6 py-4 flex items-center gap-3">
        <Sparkles size={22} />
        <p className="text-sm sm:text-base font-semibold">New member? You earn <span className="font-black">2× points</span> on your very first purchase — stack them fast!</p>
      </div>
      <h2 className="font-display text-2xl font-bold text-neutral-900 mb-4">Redeem your points</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
        {Object.entries(data.rewards).map(([id, r]) => {
          const Icon = REWARD_ICON[r.type] || Sparkles;
          const affordable = data.points >= r.cost;
          return (
            <div key={id} data-testid={`reward-${id}`} className="bg-white border border-neutral-200 rounded-2xl p-5 flex flex-col">
              <div className="w-11 h-11 rounded-xl bg-[#fff1e8] text-[#E8956B] flex items-center justify-center mb-3"><Icon size={20} /></div>
              <div className="font-bold text-neutral-900">{r.label}</div>
              <div className="text-sm text-neutral-500 mt-0.5">{r.cost.toLocaleString()} points</div>
              <button
                data-testid={`redeem-${id}`}
                onClick={() => redeem(id)}
                disabled={!affordable || redeeming === id}
                className={`mt-4 rounded-full py-2.5 font-bold text-sm transition-all flex items-center justify-center gap-2 ${affordable ? "bg-[#E8956B] hover:bg-[#CE744A] text-white" : "bg-neutral-100 text-neutral-400 cursor-not-allowed"}`}
              >
                {redeeming === id ? <Loader2 size={15} className="animate-spin" /> : affordable ? "Redeem" : `Need ${(r.cost - data.points).toLocaleString()} more`}
              </button>
            </div>
          );
        })}
      </div>

      {/* EARN + REFERRAL */}
      <div className="grid md:grid-cols-2 gap-6 mb-12">
        <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-6">
          <h3 className="font-display text-lg font-bold text-neutral-900 mb-3 flex items-center gap-2"><TrendingUp size={18} className="text-[#E8956B]" /> How to earn</h3>
          <ul className="space-y-2 text-sm text-neutral-600">
            <li className="flex justify-between"><span>Every $1 you spend</span><span className="font-bold text-neutral-900">+{data.earn_rules.per_dollar} pt</span></li>
            <li className="flex justify-between"><span>Write a review</span><span className="font-bold text-neutral-900">+{data.earn_rules.review} pts</span></li>
            <li className="flex justify-between"><span>Refer a friend</span><span className="font-bold text-neutral-900">+{data.earn_rules.referral} pts</span></li>
            <li className="flex justify-between"><span>Your first listing</span><span className="font-bold text-neutral-900">+{data.earn_rules.first_listing} pts</span></li>
            <li className="flex justify-between"><span>Complete a sale</span><span className="font-bold text-neutral-900">+{data.earn_rules.sale} pts</span></li>
          </ul>
        </div>
        <div className="bg-neutral-50 border border-neutral-200 rounded-3xl p-6">
          <h3 className="font-display text-lg font-bold text-neutral-900 mb-2 flex items-center gap-2"><Sparkles size={18} className="text-[#E8956B]" /> Invite friends</h3>
          <p className="text-sm text-neutral-500 mb-3">Share your link — earn <b>{data.earn_rules.referral} points</b> when a friend joins.</p>
          <div className="flex gap-2">
            <input readOnly value={`${window.location.origin}/?ref=${data.referral_code}`} className="flex-1 min-w-0 bg-white border border-neutral-300 rounded-lg px-3 py-2 text-xs text-neutral-700" />
            <button data-testid="copy-referral-btn" onClick={copyRef} className="bg-[#E8956B] hover:bg-[#CE744A] text-white rounded-lg px-3 shrink-0"><Copy size={16} /></button>
          </div>
        </div>
      </div>

      {/* HISTORY */}
      {data.ledger.length > 0 && (
        <div>
          <h2 className="font-display text-xl font-bold text-neutral-900 mb-4">Recent activity</h2>
          <div className="bg-white border border-neutral-200 rounded-2xl divide-y divide-neutral-100" data-testid="points-ledger">
            {data.ledger.map((l) => (
              <div key={l.id} className="flex items-center justify-between px-4 py-3 text-sm">
                <div>
                  <div className="font-medium text-neutral-800">{l.reason}</div>
                  <div className="text-xs text-neutral-400">{new Date(l.created_at).toLocaleDateString()}</div>
                </div>
                {l.delta !== 0 && <span className={`font-bold ${l.delta > 0 ? "text-green-600" : "text-neutral-500"}`}>{l.delta > 0 ? "+" : ""}{l.delta} pts</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
